import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAction } from "wasp/client/operations";
import { generateSocialReport } from "wasp/client/operations";
import { trackSocialReportGenerated } from "../../client/utils/posthog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../client/components/ui/dialog";
import { Button } from "../../client/components/ui/button";
import { Label } from "../../client/components/ui/label";
import useTranslation from "../../client/hooks/useTranslation";
import type { ReviewPeriod, ReportScope } from "../metrics/types";
import { ScopeSelector } from "./ScopeSelector";
import {
  subMonths,
  subQuarters,
  subYears,
  addMonths,
  addQuarters,
  addYears,
  startOfMonth, startOfQuarter, startOfYear,
} from "date-fns";
import { PeriodNavBar } from "./PeriodNavBar";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful generation (e.g. to refetch the reports list). */
  onGenerated?: () => void;
};

const PERIODS: ReviewPeriod[] = ["monthly", "quarterly", "annual"];

export function GenerateReportDialog({ open, onOpenChange, onGenerated }: Props) {
  const t = useTranslation();
  const navigate = useNavigate();

  const [period, setPeriod] = useState<ReviewPeriod>("monthly");
  const [scope, setScope] = useState<ReportScope>({ type: "all" });
  const [referenceDate, setReferenceDate] = useState<Date>(new Date());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useAction(generateSocialReport);

  const scopeReady = scope.type === "all" || scope.type === "all-inactive" || !!scope.id;
  const canGenerate = scopeReady && !busy;

  const periodLabel: Record<ReviewPeriod, string> = {
    monthly: t.reports.periodMonthly,
    quarterly: t.reports.periodQuarterly,
    annual: t.reports.periodAnnual,
  };

  function isCurrentPeriod(d: Date): boolean {
    const now = new Date();
    switch (period) {
      case "monthly":   return startOfMonth(d) >= startOfMonth(now);
      case "quarterly": return startOfQuarter(d) >= startOfQuarter(now);
      case "annual":    return startOfYear(d) >= startOfYear(now);
    }
  }

  function handlePrev() {
    setReferenceDate((d) => {
      switch (period) {
        case "monthly":   return subMonths(d, 1);
        case "quarterly": return subQuarters(d, 1);
        case "annual":    return subYears(d, 1);
      }
    });
  }

  function handleNext() {
    setReferenceDate((d) => {
      switch (period) {
        case "monthly":   return addMonths(d, 1);
        case "quarterly": return addQuarters(d, 1);
        case "annual":    return addYears(d, 1);
      }
    });
  }

  async function handleGenerate() {
    setBusy(true);
    setError(null);
    try {
      const r = await generate({
        period,
        scope,
        referenceDate: referenceDate.toISOString(),
      });
      trackSocialReportGenerated("story");
      onGenerated?.();
      onOpenChange(false);
      navigate(`/reports/${r.reportId}`);
    } catch (e: any) {
      setError(e?.message ?? t.reports.error);
    } finally {
      setBusy(false);
    }
  }

  function handleClose(nextOpen: boolean) {
    if (!nextOpen) {
      setError(null);
      setPeriod("monthly");
      setScope({ type: "all" });
      setReferenceDate(new Date());
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.reports.dialogTitle}</DialogTitle>
          <DialogDescription>{t.reports.dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Period */}
          <div className="space-y-2">
            <Label>{t.reports.period}</Label>
            <div className="flex flex-wrap gap-2">
              {PERIODS.map((p) => (
                <Button
                  key={p}
                  type="button"
                  variant={period === p ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setPeriod(p); setReferenceDate(new Date()); }}
                >
                  {periodLabel[p]}
                </Button>
              ))}
            </div>
            <PeriodNavBar
              period={period}
              referenceDate={referenceDate}
              onChange={setReferenceDate}
              onPrev={handlePrev}
              onNext={handleNext}
              disableNext={isCurrentPeriod(referenceDate)}
            />
          </div>

          {/* Scope */}
          <ScopeSelector value={scope} onChange={setScope} />

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)}>{t.common.close}</Button>
          <Button onClick={handleGenerate} disabled={!canGenerate}>
            {busy ? t.reports.generating : t.reports.generate}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
